// =============================================================================
// VandieZ admin-web -- Support mediation queue (/support/tickets)
// =============================================================================
// Spec 6 Plan D. Lists support call tickets raised when a shipper bails out
// of a trip after the second pickup-confirmation attempt. Admin can:
//   * Approve - clears the shipper's mediation flag so they may reopen bids.
//   * Take over - admin assigns a specific driver (UUID input); server
//                 accepts that driver's existing bid and builds a trip.
//
// Mirrors DisputesPage.tsx structure: filter tabs (open / in_progress /
// closed / all) + paginated table (50/page) + per-row inline actions.
// =============================================================================

import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Headphones,
  Loader2,
  PhoneCall,
} from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";

import {
  supportCallbacksAdminApi,
  supportTicketsAdminApi,
} from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { TableSkeleton } from "@/components/ui/skeleton";
import type { SupportCallbackRow } from "@/types/support";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type {
  SupportCallTicketRead,
  SupportTicketStatus,
} from "@/types/support_tickets";

const PAGE_SIZE = 50;

type FilterValue = SupportTicketStatus | "all";

const STATUS_FILTERS: { label: string; value: FilterValue }[] = [
  { label: "Open", value: "open" },
  { label: "In progress", value: "in_progress" },
  { label: "Closed", value: "closed" },
  { label: "All", value: "all" },
];

const STATUS_PILL: Record<SupportTicketStatus, string> = {
  open: "bg-warning-subtle text-warning-subtle-foreground",
  in_progress: "bg-primary-subtle text-primary-subtle-foreground",
  closed: "bg-success-subtle text-success-subtle-foreground",
};

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function shortId(id: string): string {
  return `${id.slice(0, 8)}…`;
}

export function SupportTicketsPage(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [filter, setFilter] = useState<FilterValue>("open");
  const [items, setItems] = useState<SupportCallTicketRead[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Per-row state for take-over driver UUID + busy flag.
  const [driverIdInputs, setDriverIdInputs] = useState<
    Record<string, string>
  >({});
  const [rowBusy, setRowBusy] = useState<Record<string, boolean>>({});
  const [rowError, setRowError] = useState<Record<string, string | null>>({});

  const fetchPage = useCallback(
    async (nextOffset: number, signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const res = await supportTicketsAdminApi.list(
          accessToken,
          {
            limit: PAGE_SIZE,
            offset: nextOffset,
            status: filter,
          },
          signal,
        );
        setItems(res.items);
        setTotal(res.total);
        setOffset(res.offset);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        const msg =
          e instanceof ApiError ? e.problem.detail : "Unexpected error.";
        setError(msg);
      } finally {
        setLoading(false);
      }
    },
    [accessToken, filter],
  );

  useEffect(() => {
    const controller = new AbortController();
    void fetchPage(0, controller.signal);
    return () => {
      controller.abort();
    };
  }, [fetchPage]);

  const setRowBusyFor = (id: string, busy: boolean): void => {
    setRowBusy((prev) => ({ ...prev, [id]: busy }));
  };

  const setRowErrorFor = (id: string, msg: string | null): void => {
    setRowError((prev) => ({ ...prev, [id]: msg }));
  };

  const handleApprove = async (ticket: SupportCallTicketRead): Promise<void> => {
    if (accessToken === null) {
      return;
    }
    setRowBusyFor(ticket.id, true);
    setRowErrorFor(ticket.id, null);
    try {
      await supportTicketsAdminApi.decide(accessToken, ticket.id, {
        decision: "approved",
      });
      // Refresh the current page to reflect the new status.
      await fetchPage(offset);
    } catch (e: unknown) {
      const msg =
        e instanceof ApiError ? e.problem.detail : "Unable to approve ticket.";
      setRowErrorFor(ticket.id, msg);
    } finally {
      setRowBusyFor(ticket.id, false);
    }
  };

  const handleTakeOver = async (ticket: SupportCallTicketRead): Promise<void> => {
    if (accessToken === null) {
      return;
    }
    const driverId = (driverIdInputs[ticket.id] ?? "").trim();
    if (driverId.length === 0) {
      setRowErrorFor(
        ticket.id,
        "Enter the driver UUID before taking over.",
      );
      return;
    }
    setRowBusyFor(ticket.id, true);
    setRowErrorFor(ticket.id, null);
    try {
      await supportTicketsAdminApi.decide(accessToken, ticket.id, {
        decision: "taken_over",
        assigned_driver_id: driverId,
      });
      await fetchPage(offset);
    } catch (e: unknown) {
      const msg =
        e instanceof ApiError ? e.problem.detail : "Unable to take over ticket.";
      setRowErrorFor(ticket.id, msg);
    } finally {
      setRowBusyFor(ticket.id, false);
    }
  };

  const canPrev = offset > 0;
  const canNext = offset + items.length < total;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Support mediation"
        description="Tickets raised when a shipper bails out of a trip and the system escalates to mediation."
      />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Headphones className="h-5 w-5" /> Support call tickets
            </CardTitle>
            <CardDescription>
              Tickets raised when a shipper bails out of a trip and the
              system escalates to mediation. Oldest open first.
              {total > 0 && (
                <span className="ml-1 text-foreground">
                  Showing {offset + 1}–{offset + items.length} of {total}.
                </span>
              )}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4 flex gap-1">
              {STATUS_FILTERS.map((f) => (
                <button
                  key={f.value}
                  onClick={() => {
                    setFilter(f.value);
                  }}
                  className={cn(
                    "rounded px-3 py-1 text-sm",
                    filter === f.value
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground hover:bg-muted",
                  )}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {error !== null && (
              <Alert variant="destructive" className="mb-4">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {loading && items.length === 0 ? (
              <div className="flex items-center gap-2 py-8 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : items.length === 0 ? (
              <div className="py-10 text-center text-muted-foreground">
                Nothing in this view right now.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
                    <tr>
                      <th className="py-2 pr-4">Created</th>
                      <th className="py-2 pr-4">Shipper</th>
                      <th className="py-2 pr-4">Load</th>
                      <th className="py-2 pr-4">Reason</th>
                      <th className="py-2 pr-4">Status</th>
                      <th className="py-2 pr-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((row) => {
                      const isClosed = row.status === "closed";
                      const busy = rowBusy[row.id] === true;
                      const rowErr = rowError[row.id] ?? null;
                      const driverIdValue = driverIdInputs[row.id] ?? "";
                      return (
                        <tr
                          key={row.id}
                          className="border-b align-top last:border-b-0"
                        >
                          <td className="py-3 pr-4 text-xs text-muted-foreground">
                            {formatDate(row.created_at)}
                          </td>
                          <td className="py-3 pr-4">
                            <div className="font-medium">
                              {row.shipper_name ?? "—"}
                            </div>
                            <div className="text-xs text-muted-foreground font-mono">
                              {row.shipper_phone ?? shortId(row.shipper_id)}
                            </div>
                          </td>
                          <td className="py-3 pr-4 font-mono text-xs">
                            {shortId(row.load_id)}
                          </td>
                          <td className="py-3 pr-4 max-w-[200px]">
                            <span className="text-xs">
                              {row.cancel_reason ?? "—"}
                            </span>
                          </td>
                          <td className="py-3 pr-4">
                            <span
                              className={cn(
                                "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                                STATUS_PILL[row.status],
                              )}
                            >
                              {row.status.replace(/_/g, " ")}
                            </span>
                            {isClosed && row.decision !== null && (
                              <div className="mt-1 text-xs text-muted-foreground capitalize">
                                {row.decision.replace(/_/g, " ")}
                              </div>
                            )}
                          </td>
                          <td className="py-3 pr-4">
                            {isClosed ? (
                              <span className="text-xs text-muted-foreground">
                                Decided{" "}
                                {row.decided_at !== null
                                  ? formatDate(row.decided_at)
                                  : ""}
                              </span>
                            ) : (
                              <div className="flex flex-col gap-2">
                                <Button
                                  variant="default"
                                  size="sm"
                                  disabled={busy}
                                  onClick={() => void handleApprove(row)}
                                >
                                  {busy ? (
                                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                  ) : null}
                                  Approve
                                </Button>
                                <div className="flex gap-1">
                                  <Input
                                    placeholder="Driver UUID"
                                    className="h-8 text-xs"
                                    value={driverIdValue}
                                    onChange={(e) => {
                                      const v = e.target.value;
                                      setDriverIdInputs((prev) => ({
                                        ...prev,
                                        [row.id]: v,
                                      }));
                                    }}
                                    disabled={busy}
                                  />
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={busy}
                                    onClick={() => void handleTakeOver(row)}
                                  >
                                    Take over
                                  </Button>
                                </div>
                                {rowErr !== null && (
                                  <span className="text-xs text-destructive">
                                    {rowErr}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="mt-4 flex items-center justify-between">
              <Button
                variant="outline"
                size="sm"
                disabled={!canPrev || loading}
                onClick={() => void fetchPage(Math.max(0, offset - PAGE_SIZE))}
              >
                <ChevronLeft className="mr-1 h-4 w-4" /> Previous
              </Button>
              <span className="text-xs text-muted-foreground">
                {loading
                  ? "Loading…"
                  : `Page ${Math.floor(offset / PAGE_SIZE) + 1}`}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={!canNext || loading}
                onClick={() => void fetchPage(offset + PAGE_SIZE)}
              >
                Next <ChevronRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        <SupportCallbacksCard />
    </div>
  );
}

/// Pre-auth callback leads (Spec 10 T4).
///
/// A separate queue from the tickets above and it cannot be merged into
/// them: these rows come from the login screen, from somebody who could
/// not get in, so they carry a name and a phone number and no user id.
///
/// It had no admin surface at all until 2026-08-11 -- the endpoint was
/// built, tested and never called, so every person who asked to be rung
/// back was recorded and never seen by anyone.
function SupportCallbacksCard(): React.JSX.Element {
  const { accessToken } = useAuth();
  const [rows, setRows] = useState<SupportCallbackRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showHandled, setShowHandled] = useState<boolean>(false);

  const load = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      if (accessToken === null) {return;}
      try {
        const res = await supportCallbacksAdminApi.list(
          accessToken,
          { status: showHandled ? "handled" : "open", page_size: 50 },
          signal,
        );
        setRows(res.items);
        setError(null);
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {return;}
        setError(
          e instanceof ApiError
            ? e.problem.detail
            : "Couldn't load callback requests.",
        );
      } finally {
        setLoading(false);
      }
    },
    [accessToken, showHandled],
  );

  useEffect(() => {
    const c = new AbortController();
    void load(c.signal);
    return () => { c.abort(); };
  }, [load]);

  const markHandled = async (row: SupportCallbackRow): Promise<void> => {
    if (accessToken === null) {return;}
    setBusyId(row.id);
    try {
      await supportCallbacksAdminApi.handle(accessToken, row.id);
      await load();
    } catch (e: unknown) {
      setError(
        e instanceof ApiError ? e.problem.detail : "Couldn't update it.",
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <PhoneCall className="h-5 w-5" /> Callback requests
        </CardTitle>
        <CardDescription>
          People who asked to be rung back from the login screen, before
          they had an account. Not linked to a user or a trip — the
          phone number is the whole lead.
          <span className="ml-1 text-foreground">
            {rows.length} {showHandled ? "handled" : "open"}.
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-3 flex items-center gap-2">
          <Button
            variant={showHandled ? "outline" : "default"}
            size="sm"
            onClick={() => { setShowHandled(false); }}
          >
            Open
          </Button>
          <Button
            variant={showHandled ? "default" : "outline"}
            size="sm"
            onClick={() => { setShowHandled(true); }}
          >
            Handled
          </Button>
        </div>

        {error !== null && (
          <Alert variant="destructive" className="mb-3">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {loading && rows.length === 0 ? (
          <TableSkeleton rows={4} columns={4} />
        ) : rows.length === 0 ? (
          <div className="py-10 text-center text-muted-foreground text-sm">
            {showHandled
              ? "Nothing handled yet."
              : "No open callback requests."}
          </div>
        ) : (
          <ul className="divide-y">
            {rows.map((r) => (
              <li
                key={r.id}
                className="flex items-start gap-3 px-2 py-3"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-sm">
                      {r.full_name}
                    </span>
                    <a
                      href={`tel:${r.phone}`}
                      className="font-mono text-primary text-xs hover:underline"
                    >
                      {r.phone}
                    </a>
                    <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground text-xs">
                      {r.source}
                    </span>
                  </div>
                  {r.note !== null && r.note.length > 0 && (
                    <p className="mt-0.5 text-muted-foreground text-xs">
                      {r.note}
                    </p>
                  )}
                  <p className="mt-0.5 text-muted-foreground text-xs">
                    {new Date(r.created_at).toLocaleString()}
                  </p>
                </div>
                {r.status === "open" && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={busyId === r.id}
                    onClick={() => void markHandled(r)}
                  >
                    Mark handled
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
